import React, { useState, useRef } from 'react';
import { useToast } from './Toast';
import { apiUrl } from '../config/api';
import './OptimizationToolbox.css';

interface OptimizationStats {
  originalSize: number;
  optimizedSize: number;
  totalSavings: number;
  totalSavingsPercent: number;
  fileResults: Record<string, {
    originalSize: number;
    optimizedSize: number;
    savings: number;
    savingsPercent: number;
  }>;
}

export const OptimizationToolbox: React.FC = () => {
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<OptimizationStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Options
  const [imageQuality, setImageQuality] = useState(80);
  const [minifyCSS, setMinifyCSS] = useState(true);
  const [minifyJS, setMinifyJS] = useState(true);
  const [stripMetadata, setStripMetadata] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.zip')) {
      setFile(droppedFile);
      setStats(null);
      setError(null);
    } else {
      setError('Please upload a ZIP file');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setStats(null);
      setError(null);
    }
  };

  const analyzeFile = async () => {
    if (!file) {
      toast('Please upload a ZIP file first', 'warning');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('imageQuality', imageQuality.toString());
      formData.append('minifyCSS', minifyCSS.toString());
      formData.append('minifyJS', minifyJS.toString());
      formData.append('stripMetadata', stripMetadata.toString());

      console.log('Analyzing file:', file.name);
      console.log('Sending to:', apiUrl('/optimize/stats'));

      const response = await fetch(apiUrl('/optimize/stats'), {
        method: 'POST',
        body: formData,
        credentials: 'include'
        // Note: multipart/form-data is automatically set by browser and CSRF is skipped for file uploads
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Stats data:', data);
      setStats(data);
    } catch (err: any) {
      console.error('Analyze error:', err);
      setError(err.message || 'Failed to analyze file');
    } finally {
      setLoading(false);
    }
  };

  const optimizeFile = async () => {
    if (!file) {
      toast('Please upload a ZIP file first', 'warning');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('imageQuality', imageQuality.toString());
      formData.append('minifyCSS', minifyCSS.toString());
      formData.append('minifyJS', minifyJS.toString());
      formData.append('stripMetadata', stripMetadata.toString());

      console.log('Optimizing file:', file.name);
      console.log('Sending to:', apiUrl('/optimize'));

      const response = await fetch(apiUrl('/optimize'), {
        method: 'POST',
        body: formData,
        credentials: 'include'
        // Note: multipart/form-data is automatically set by browser and CSRF is skipped for file uploads
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Server error: ${response.status}`);
      }

      // Get optimization stats from headers
      const originalSize = parseInt(response.headers.get('X-Original-Size') || '0', 10);
      const optimizedSize = parseInt(response.headers.get('X-Optimized-Size') || '0', 10);
      const savings = parseInt(response.headers.get('X-Savings-Bytes') || '0', 10);
      const savingsPercent = parseFloat(response.headers.get('X-Savings-Percent') || '0');

      console.log('Optimization stats:', { originalSize, optimizedSize, savings, savingsPercent });

      // Download optimized file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `optimized-${file.name}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Show success message
      toast(`Optimization complete! Saved ${formatBytes(savings)} (${savingsPercent.toFixed(1)}%)`, 'success');

    } catch (err: any) {
      console.error('Optimize error:', err);
      setError(err.message || 'Optimization failed');
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="optimization-toolbox">
      <div className="toolbox-header">
        <h2>🛠️ Optimization Toolbox</h2>
        <p>Compress images, minify code, and optimize your creative packages</p>
      </div>

      <div 
        className={`upload-zone ${isDragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input 
          ref={fileInputRef}
          type="file" 
          accept=".zip" 
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        {!file ? (
          <>
            <div className="upload-icon">📦</div>
            <p className="upload-text">Drop your creative ZIP here or click to browse</p>
            <p className="upload-hint">Only ZIP files are supported</p>
          </>
        ) : (
          <>
            <div className="file-icon">✓</div>
            <p className="file-name">{file.name}</p>
            <p className="file-size">{formatBytes(file.size)}</p>
            <button className="change-file-btn" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
              Change File
            </button>
          </>
        )}
      </div>

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      <div className="optimization-options">
        <h3>Optimization Settings</h3>

        <div className="option-group">
          <label>
            <span>Image Quality</span>
            <span className="quality-value">{imageQuality}%</span>
          </label>
          <input 
            type="range" 
            min="60" 
            max="100" 
            value={imageQuality}
            onChange={(e) => setImageQuality(parseInt(e.target.value, 10))}
            className="quality-slider"
          />
          <div className="slider-labels">
            <span>Small Size</span>
            <span>High Quality</span>
          </div>
        </div>

        <div className="option-group checkbox-group">
          <label className="checkbox-label">
            <input 
              type="checkbox" 
              checked={minifyCSS}
              onChange={(e) => setMinifyCSS(e.target.checked)}
            />
            <span>Minify CSS files</span>
          </label>

          <label className="checkbox-label">
            <input 
              type="checkbox" 
              checked={minifyJS}
              onChange={(e) => setMinifyJS(e.target.checked)}
            />
            <span>Minify JavaScript files</span>
          </label>

          <label className="checkbox-label">
            <input 
              type="checkbox" 
              checked={stripMetadata}
              onChange={(e) => setStripMetadata(e.target.checked)}
            />
            <span>Strip image metadata (EXIF data)</span>
          </label>
        </div>
      </div>

      <div className="action-buttons">
        <button 
          className="btn btn-secondary"
          onClick={analyzeFile}
          disabled={loading}
        >
          {loading ? 'Analyzing...' : '📊 Analyze Potential Savings'}
        </button>

        <button 
          className="btn btn-primary"
          onClick={optimizeFile}
          disabled={loading}
        >
          {loading ? 'Optimizing...' : '⚡ Optimize & Download'}
        </button>
      </div>

      {stats && (
        <div className="optimization-results">
          <h3>Optimization Analysis</h3>

          <div className="savings-summary">
            <div className="summary-card">
              <div className="card-label">Original Size</div>
              <div className="card-value">{formatBytes(stats.originalSize)}</div>
            </div>

            <div className="summary-arrow">→</div>

            <div className="summary-card">
              <div className="card-label">Optimized Size</div>
              <div className="card-value">{formatBytes(stats.optimizedSize)}</div>
            </div>

            <div className="summary-card savings">
              <div className="card-label">Total Savings</div>
              <div className="card-value">
                {formatBytes(stats.totalSavings)}
                <span className="percent">({stats.totalSavingsPercent.toFixed(1)}%)</span>
              </div>
            </div>
          </div>

          {stats.fileResults && Object.keys(stats.fileResults).length > 0 && (
            <div className="file-breakdown">
              <h4>File-by-File Breakdown</h4>
              <div className="file-list">
                {Object.entries(stats.fileResults).map(([fileName, result]) => (
                  <div key={fileName} className="file-result">
                    <div className="file-result-name">{fileName}</div>
                    <div className="file-result-stats">
                      <span>{formatBytes(result.originalSize)} → {formatBytes(result.optimizedSize)}</span>
                      <span className="savings-badge">-{result.savingsPercent.toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
